import * as React from "react";
import { AppDefn, usePlayerInThisGame } from "~lib/App";
import {
  Loading,
  useInfo,
  useWrappedConnection,
} from "~lib/ConnectionProvider";
import { CoopPokerConnection } from "./CoopPokerConnection";
import {
  CoopPokerClient,
  CoopPokerServer,
  GameInfo,
} from "~shared/namespace/CoopPoker";
import { AppName } from "~shared/messageTypes";
import { ConnectionState } from "~/App";
import { StreetName, getRound } from "~shared/lib/PokerGames";
import { BaseClient, EndGameAction } from "~shared/namespace/Base";
import { Utils } from "~lib/Utils";
import {
  Hand,
  GalleryVertical,
  BarChart3,
  Trophy,
  Frown,
  Pause,
  RefreshCw,
  Home,
} from "lucide-react";

export const colorFromPosition = (
  street: StreetName,
  index: number,
  max: number
) => {
  const t = max <= 1 ? 0 : index / max;

  const hue = (HUES[street] + t * 120) % 360;

  const saturation = 70;
  const lightness = 65 - t * 25;

  return {
    backgroundColor: `hsl(${hue} ${saturation}% ${lightness}%)`,
    color: lightness < 50 ? "#ffffff" : "#000000",
    textShadow:
      lightness < 50
        ? "0 1px 2px rgba(0,0,0,0.5)"
        : "0 1px 2px rgba(255,255,255,0.5)",
  };
};

export const HUES = {
  [StreetName.PRE_FLOP]: 60,
  [StreetName.FLOP]: 40,
  [StreetName.TURN]: 20,
  [StreetName.RIVER]: 0,
  [StreetName.SHOWDOWN]: 285,
};

export const CoopPokerApp: AppDefn<{
  inGame: boolean;
  connection: CoopPokerConnection | undefined;
  gameInfo: GameInfo | undefined;
}> = {
  hooks(active, { connectionState }) {
    const inGame = usePlayerInThisGame(AppName.COOP_POKER);
    const connection = useWrappedConnection(inGame, CoopPokerConnection);
    const gameInfo = useInfo(
      connectionState === ConnectionState.READY && active,
      CoopPokerServer.TypeValue.GAME_INFO
    );
    return {
      inGame,
      connection,
      gameInfo,
    };
  },
  app({ inGame, player, connection, gameInfo, time, roomInfo }) {
    if (!gameInfo || !player) return <Loading />;
    const round = getRound(gameInfo.board.length);
    const playerCount = Object.keys(gameInfo.claims).length;
    const selfClaim = (gameInfo.claims[player.id] ?? [0, 0, 0, 0, 0])[round];
    return (
      <div className="space-y-2.5 p-3 overflow-y-auto max-h-screen">
        {gameInfo.self && (
          <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 backdrop-blur-sm rounded-xl p-3 border-2 border-purple-400/40 shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <h2 className="mb-2 flex items-center gap-2 text-base">
                  <Hand className="text-purple-300" size={18} />
                  Your Hand
                </h2>
                <div className="bg-black/20 rounded-lg p-2 inline-block">
                  <div>{Utils.prettifyCards(gameInfo.self.cards)}</div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2">
                {gameInfo.self.forceClaimAt > time && selfClaim === 0 && (
                  <div className="bg-red-500/20 border-2 border-red-500/50 rounded-lg px-3 py-1">
                    <p className="text-red-200 font-semibold text-xs whitespace-nowrap">
                      ⏱️{" "}
                      {Math.round((gameInfo.self.forceClaimAt - time) / 1000)}s
                    </p>
                  </div>
                )}

                {selfClaim !== 0 ? (
                  <div className="text-center">
                    <p className="text-xs mb-1">Your claim:</p>
                    <span
                      className="claim-button inline-flex items-center justify-center"
                      style={colorFromPosition(
                        round,
                        selfClaim - 1,
                        playerCount
                      )}
                    >
                      {selfClaim}
                    </span>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-sm mb-2 font-semibold">Choose claim:</p>
                    <div className="flex flex-wrap gap-1.5 max-w-[180px]">
                      {Array.from({ length: playerCount }, (_, i) => (
                        <button
                          className="claim-button"
                          style={colorFromPosition(round, i, playerCount)}
                          key={`claim${i + 1}`}
                          disabled={!connection}
                          onClick={() => {
                            if (!connection) return;
                            connection.sendMessageWithType(
                              CoopPokerClient.TypeValue.CLAIM,
                              {
                                claim: i + 1,
                              }
                            );
                          }}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/30 backdrop-blur-sm rounded-xl p-3 border-2 border-blue-400/40">
          <h2 className="mb-2 flex items-center gap-2 text-base">
            <GalleryVertical className="text-blue-300" size={18} />
            Board
          </h2>
          <div
            className="bg-black/20 rounded-lg p-2 w-full flex items-center justify-center"
            style={{ minHeight: gameInfo.board.length > 0 ? "auto" : "60px" }}
          >
            {gameInfo.board.length > 0 ? (
              <div className="flex gap-2">
                {Utils.prettifyCards(gameInfo.board)}
              </div>
            ) : (
              <span className="text-gray-400 italic text-sm">
                Waiting for cards...
              </span>
            )}
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border-2 border-white/10">
          <h2 className="mb-2 flex items-center gap-2 text-base">
            <BarChart3 className="text-green-300" size={18} />
            Player Claims & Results
          </h2>
          <div className="space-y-1.5">
            {roomInfo &&
              Object.keys(gameInfo.claims).map((x) => {
                const result = gameInfo.result
                  ? gameInfo.result.players[x]
                  : undefined;
                const playerName =
                  roomInfo.players.find((y) => y.id === x)?.name ?? `[?${x}]`;
                const isCurrentPlayer = x === player?.id;
                return (
                  <div
                    key={x}
                    className="player-card flex flex-wrap items-center gap-2 py-1 px-2"
                  >
                    <span
                      className={`font-bold text-sm min-w-[100px] flex items-center gap-1 ${
                        isCurrentPlayer ? "text-yellow-300" : ""
                      }`}
                    >
                      {playerName}:
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      {gameInfo.claims[x]
                        .filter((x) => x)
                        .map((c, i) => (
                          <span
                            key={i}
                            className="claim-button inline-flex items-center justify-center w-8 h-8 text-xs"
                            style={colorFromPosition(i, c - 1, playerCount)}
                          >
                            {c}
                          </span>
                        ))}
                      {gameInfo.claims[x].filter((x) => x).length === 0 && (
                        <span className="text-gray-400 italic">
                          No claims yet
                        </span>
                      )}
                      {result && (
                        <div className="flex items-center gap-2 ml-2 pl-2 border-l-2 border-white/20">
                          <div className="flex gap-1">
                            {Utils.prettifyCards(result.cards)}
                          </div>
                          <span
                            className="claim-button inline-flex items-center justify-center w-8 h-8 text-xs"
                            style={colorFromPosition(
                              StreetName.SHOWDOWN,
                              result.position - 1,
                              playerCount
                            )}
                          >
                            {result.position}
                          </span>
                          {result.position !== result.rank && (
                            <span className="text-yellow-300 font-semibold">
                              (={result.rank})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {gameInfo.result && (
          <div
            className={`backdrop-blur-sm rounded-xl p-2.5 border-2 ${
              gameInfo.result.isGameWon
                ? "bg-green-600/30 border-green-400/50"
                : "bg-red-600/30 border-red-400/50"
            }`}
          >
            <p className="text-lg font-bold mb-1.5 flex items-center gap-2">
              {gameInfo.result.isGameWon ? (
                <>
                  <Trophy size={20} className="text-yellow-300" />
                  Victory!
                </>
              ) : (
                <>
                  <Frown size={20} className="text-red-300" />
                  Defeat
                </>
              )}
            </p>
            {gameInfo.result.nextGameAt && (
              <p className="text-xs mb-1.5">
                Next round in{" "}
                {Math.round((gameInfo.result.nextGameAt - time) / 1000)}s
              </p>
            )}
            {inGame && connection && (
              <div className="flex flex-wrap gap-1.5">
                {gameInfo.result.nextGameAt && (
                  <button
                    onClick={() =>
                      connection.sendMessageWithType(
                        BaseClient.TypeValue.END_GAME_ACTION,
                        EndGameAction.WAIT
                      )
                    }
                  >
                    <Pause className="inline-block mr-1.5" size={16} />
                    Wait
                  </button>
                )}
                <button
                  onClick={() =>
                    connection.sendMessageWithType(
                      BaseClient.TypeValue.END_GAME_ACTION,
                      EndGameAction.NEXT_GAME
                    )
                  }
                >
                  <RefreshCw className="inline-block mr-1.5" size={16} />
                  New Round
                </button>
                <button
                  className="destructive"
                  onClick={() =>
                    connection.sendMessageWithType(
                      BaseClient.TypeValue.END_GAME_ACTION,
                      EndGameAction.RETURN_TO_LOBBY
                    )
                  }
                >
                  <Home className="inline-block mr-1.5" size={16} />
                  Back to Lobby
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
};
